/**
 * Advertising slots at real places along each route. `at` is metres from the route start (game scale);
 * `side` is the physical side of the road on the outbound trip ('kerb' = our kerb side going out,
 * 'far' = across the median), so on the way back the same board is on the other side.
 * Shared so the server knows which slots exist when bookings and impressions come in.
 */
export type AdFormat = 'billboard' | 'gantry';

export interface AdSlot {
  id: string;
  route: string;
  /** Leg name shown in reports (e.g. "Shiashie"). */
  area: string;
  at: number;
  side: 'kerb' | 'far';
  format: AdFormat;
}

export const AD_SLOTS: readonly AdSlot[] = [
  { id: 'ma-madina-1', route: 'madina-accra', area: 'Madina', at: 500, side: 'kerb', format: 'billboard' },
  { id: 'ma-legon-1', route: 'madina-accra', area: 'Legon', at: 1340, side: 'far', format: 'billboard' },
  { id: 'ma-shiashie-1', route: 'madina-accra', area: 'Shiashie', at: 2140, side: 'kerb', format: 'billboard' },
  { id: 'ma-shiashie-2', route: 'madina-accra', area: 'Shiashie', at: 2300, side: 'far', format: 'billboard' },
  { id: 'ma-airport-gantry', route: 'madina-accra', area: 'Airport', at: 2860, side: 'kerb', format: 'gantry' },
  { id: 'ma-airport-1', route: 'madina-accra', area: 'Airport', at: 3200, side: 'kerb', format: 'billboard' },
  { id: 'ma-airport-2', route: 'madina-accra', area: 'Airport', at: 3560, side: 'far', format: 'billboard' },
  { id: 'ma-liberation-1', route: 'madina-accra', area: 'Liberation Road', at: 4120, side: 'kerb', format: 'billboard' },
  { id: 'ma-ridge-1', route: 'madina-accra', area: 'Ridge', at: 5080, side: 'far', format: 'billboard' },
  { id: 'ca-circle-1', route: 'circle-airport', area: 'Circle', at: 300, side: 'kerb', format: 'billboard' },
  { id: 'ca-ringroad-1', route: 'circle-airport', area: 'Ring Road', at: 1620, side: 'far', format: 'billboard' },
  { id: 'ca-osu-1', route: 'circle-airport', area: 'Osu', at: 2560, side: 'kerb', format: 'billboard' },
  { id: 'ca-airport-gantry', route: 'circle-airport', area: 'Liberation Road', at: 4400, side: 'kerb', format: 'gantry' },
  { id: 'ka-kasoa-1', route: 'kasoa-accra', area: 'Kasoa', at: 520, side: 'kerb', format: 'billboard' },
  { id: 'ka-kaneshie-1', route: 'kasoa-accra', area: 'Kaneshie', at: 3160, side: 'far', format: 'billboard' },
  { id: 'km-achimota-1', route: 'kasoa-madina', area: 'Achimota', at: 2440, side: 'kerb', format: 'billboard' },
  { id: 'km-legon-1', route: 'kasoa-madina', area: 'Legon', at: 4620, side: 'far', format: 'billboard' },

  // Just past each traffic light, on the kerb side, so it's in front of you while you wait at red.
  { id: 'ma-madina-zongo-light', route: 'madina-accra', area: 'Madina Zongo junction', at: 410, side: 'kerb', format: 'billboard' },
  { id: 'ma-upsa-light', route: 'madina-accra', area: 'UPSA junction', at: 890, side: 'kerb', format: 'billboard' },
  { id: 'ma-okponglo-light', route: 'madina-accra', area: 'Okponglo junction', at: 1850, side: 'kerb', format: 'billboard' },
  { id: 'ma-tetteh-quarshie-light', route: 'madina-accra', area: 'Tetteh Quarshie', at: 2770, side: 'kerb', format: 'billboard' },
  { id: 'ma-airport-light', route: 'madina-accra', area: 'Airport junction', at: 3690, side: 'kerb', format: 'billboard' },
  { id: 'ma-ridge-light', route: 'madina-accra', area: 'Ridge junction', at: 4650, side: 'kerb', format: 'billboard' },
  { id: 'ma-makola-light', route: 'madina-accra', area: 'Makola junction', at: 5570, side: 'kerb', format: 'billboard' },
  { id: 'ca-kn-ave-light', route: 'circle-airport', area: 'Kwame Nkrumah Ave junction', at: 450, side: 'kerb', format: 'billboard' },
  { id: 'ca-nima-light', route: 'circle-airport', area: 'Nima junction', at: 1330, side: 'kerb', format: 'billboard' },
  { id: 'ca-kanda-light', route: 'circle-airport', area: 'Kanda junction', at: 1730, side: 'kerb', format: 'billboard' },
  { id: 'ca-oxford-light', route: 'circle-airport', area: 'Oxford Street junction', at: 2490, side: 'kerb', format: 'billboard' },
  { id: 'ca-cantonments-rd-light', route: 'circle-airport', area: 'Cantonments Road junction', at: 3370, side: 'kerb', format: 'billboard' },
  { id: 'ca-fourth-circular-light', route: 'circle-airport', area: 'Fourth Circular junction', at: 3730, side: 'kerb', format: 'billboard' },
  { id: 'ca-37-light', route: 'circle-airport', area: '37 junction', at: 4090, side: 'kerb', format: 'billboard' },
  { id: 'ka-kasoa-light', route: 'kasoa-accra', area: 'Kasoa junction', at: 410, side: 'kerb', format: 'billboard' },
  { id: 'ka-weija-light', route: 'kasoa-accra', area: 'Weija junction', at: 890, side: 'kerb', format: 'billboard' },
  { id: 'ka-mallam-light', route: 'kasoa-accra', area: 'Mallam junction', at: 1850, side: 'kerb', format: 'billboard' },
  { id: 'ka-kaneshie-light', route: 'kasoa-accra', area: 'Kaneshie junction', at: 2850, side: 'kerb', format: 'billboard' },
  { id: 'ka-circle-light', route: 'kasoa-accra', area: 'Circle junction', at: 3370, side: 'kerb', format: 'billboard' },
  { id: 'ka-makola-light', route: 'kasoa-accra', area: 'Makola junction', at: 3930, side: 'kerb', format: 'billboard' },
  { id: 'km-kasoa-light', route: 'kasoa-madina', area: 'Kasoa junction', at: 410, side: 'kerb', format: 'billboard' },
  { id: 'km-weija-light', route: 'kasoa-madina', area: 'Weija junction', at: 890, side: 'kerb', format: 'billboard' },
  { id: 'km-lapaz-light', route: 'kasoa-madina', area: 'Lapaz junction', at: 1850, side: 'kerb', format: 'billboard' },
  { id: 'km-achimota-light', route: 'kasoa-madina', area: 'Achimota junction', at: 2330, side: 'kerb', format: 'billboard' },
  { id: 'km-tetteh-quarshie-light', route: 'kasoa-madina', area: 'Tetteh Quarshie', at: 2810, side: 'kerb', format: 'billboard' },
  { id: 'km-okponglo-light', route: 'kasoa-madina', area: 'Okponglo junction', at: 3810, side: 'kerb', format: 'billboard' },
  { id: 'km-legon-light', route: 'kasoa-madina', area: 'Legon junction', at: 4770, side: 'kerb', format: 'billboard' },
  { id: 'km-madina-zongo-light', route: 'kasoa-madina', area: 'Madina Zongo junction', at: 5290, side: 'kerb', format: 'billboard' },
];

/** Minimum on-screen time for a sighting to count as one view. */
export const AD_VIEW_SECONDS = 1;

export interface AdPlacement {
  slotId: string;
  campaignId: string;
  advertiser: string;
  title: string;
  /** Same-origin path (e.g. /ads/brand.jpg) or an https URL that allows CORS. */
  imageUrl: string;
}

export interface AdImpression {
  slotId: string;
  campaignId: string;
  views: number;
  seconds: number;
}
